import { CopyOutlined } from '@ant-design/icons';
import type { MessageDescriptor } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { Tabs } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePresence } from '../hooks/usePresence';
import { useProjectAuthority } from '../hooks/useProjectAuthority';
import { errorMessage } from '../i18n/errorMessages';
import {
  addAgentToChannel,
  addGuildMember,
  createAgent,
  DEFAULT_PROJECTION_STRATEGY,
  fetchAgent,
  fetchAgentConfig,
  fetchAgentPermissions,
  fetchAgentRuntime,
  fetchGuildChannels,
  fetchGuilds,
  fetchRemoteNodes,
  fetchRemoteNodeStatus,
  getAgentChannels,
  revealAgentApiKey,
  rotateAgentApiKey,
  setAgentChannelProjectionStrategy,
  updateAgentConfig,
  updateAgentPermissions,
  updateAgentRequireMention,
  type Agent,
  type AgentChannelSummary,
  type AgentRuntime,
  type ClaudePromptStrategy,
  type Guild,
  type HostedAgentProvider,
  type PermissionDetail,
  type RemoteNode,
} from '../lib/api';
import { channelDisplayName } from '../lib/channelDisplay';
import { copyToClipboard, readClipboard } from '../lib/clipboard';
import { buildOpenClawOnboardingPlan } from '../lib/openclaw-onboarding';
import { deriveProjectAuthority } from '../lib/projectAuthority';
import { routes } from '../lib/routes';
import { getBorgeeHttpBaseUrl } from '../lib/runtime-base-url';
import { useAgentStore, useAuthStore, useChannelStore, useServerStore } from '../stores';
import type { Channel } from '../types';
import { AgentConfigPanel } from './AgentConfigPanel';
import {
  AGENT_PROVIDER_LABELS,
  AGENT_PROVIDERS,
  buildAgentConnectCommand,
  buildAgentConnectCommandParts,
  getStoredAgentProvider,
  providerHasPlatformDifference,
  setStoredAgentProvider,
  type AgentProvider,
  type Platform,
} from './AgentConnectResult';
import DecommissionAgentModal from './DecommissionAgentModal';
import HostedAgentCreated from './HostedAgentCreated';
import RuntimeCard from './RuntimeCard';
import { useToast } from './Toast';
import { UserBadge } from './UserBadge';

function formatMaskedApiKey(last4: string): string {
  return `bgr_...${last4}`;
}

const API_KEY_AUTO_CLEAR_MS = 60_000;

const KNOWN_PERMISSIONS = [
  'message.send',
  'channel.create',
  'channel.delete',
  'channel.manage_members',
  'channel.manage_visibility',
];
const permissionLabels: Record<string, MessageDescriptor> = {
  'message.send': msg`Send messages`,
  'channel.create': msg`Create channels`,
  'channel.delete': msg`Delete channels`,
  'channel.manage_members': msg`Manage channel members`,
  'channel.manage_visibility': msg`Manage channel visibility`,
};
const HOSTED_AGENT_PROVIDERS: HostedAgentProvider[] = ['claude', 'codex', 'copilot'];
const CLAUDE_PROMPT_STRATEGIES: ClaudePromptStrategy[] = [
  'session-brief',
  'turn-full',
  'message-only',
  'turn-thin',
  'file-brief',
];

function resolveConnectedHostedProvider(agent: Agent): HostedAgentProvider | null {
  const match = agent.last_connected_plugin_id?.match(/^agents-host:(claude|codex|copilot)$/);
  return match ? (match[1] as HostedAgentProvider) : null;
}

interface Props {
  onBack?: () => void;
}

interface PendingAgentPlacement {
  guildId: string;
  projectMembershipPending: boolean;
  channels: Array<{ id: string; guildId: string }>;
}

function ManagedProjectOption({ guild }: { guild: Guild }) {
  const authority = useProjectAuthority(guild.id);
  if (!authority.ready || !authority.canManageProject) return null;
  return <option value={guild.id}>{guild.name}</option>;
}

export default function AgentManager({ onBack }: Props) {
  const { i18n } = useLingui();
  const channels = useChannelStore((s) => s.channels);
  const agents = useAgentStore((s) => s.agents);
  const loadAgents = useAgentStore((s) => s.loadAgents);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [decommissionAgent, setDecommissionAgent] = useState<Agent | null>(null);

  const load = useCallback(async () => {
    try {
      await loadAgents();
    } finally {
      setLoading(false);
    }
  }, [loadAgents]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="agent-page">
      <div className="admin-section-header">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {onBack && (
            <button className="btn btn-sm" onClick={onBack}>
              <Trans>← Back</Trans>
            </button>
          )}
          <h2>
            <Trans>My Agents</Trans>
          </h2>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
          <Trans>Create Agent</Trans>
        </button>
      </div>

      {loading ? (
        <div className="app-loading">
          <div className="loading-spinner-large" />
        </div>
      ) : agents.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: 40 }}>
          <Trans>No agents yet. Create one to get started.</Trans>
        </p>
      ) : (
        agents.map((agent) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            expanded={selectedAgent === agent.id}
            onToggle={() => setSelectedAgent(selectedAgent === agent.id ? null : agent.id)}
            onDelete={() => setDecommissionAgent(agent)}
            onRefresh={load}
            channels={channels}
          />
        ))
      )}

      {showCreate && (
        <CreateAgentModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            load();
          }}
        />
      )}
      {decommissionAgent && (
        <DecommissionAgentModal
          agentId={decommissionAgent.id}
          agentName={decommissionAgent.display_name}
          agentState={decommissionAgent.state}
          lastConnectedPluginId={(decommissionAgent as any).last_connected_plugin_id}
          onClose={() => setDecommissionAgent(null)}
          onDeleted={() => {
            setDecommissionAgent(null);
            useAgentStore.getState().loadAgents();
            const guildId = useAuthStore.getState().guildId;
            if (guildId)
              void Promise.resolve(useServerStore.getState().loadMembers(guildId)).catch(
                () => undefined,
              );
          }}
        />
      )}
    </div>
  );
}

function AgentCard({
  agent,
  expanded,
  onToggle,
  onDelete,
  onRefresh,
  channels,
}: {
  agent: Agent;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onRefresh: () => void;
  channels: { id: string; name: string; type?: 'channel' | 'dm' }[];
}) {
  const { i18n } = useLingui();
  const [permissions, setPermissions] = useState<PermissionDetail[]>([]);
  const [loadingPerms, setLoadingPerms] = useState(false);

  const [requireMention, setRequireMention] = useState<boolean>(agent.require_mention ?? true);
  const [savingRequireMention, setSavingRequireMention] = useState(false);
  useEffect(() => {
    setRequireMention(agent.require_mention ?? true);
  }, [agent.require_mention]);

  const [last4, setLast4] = useState<string | null>(null);
  const [loadingKey, setLoadingKey] = useState(false);
  const [copying, setCopying] = useState(false);
  const [connectProvider, setConnectProvider] = useState<AgentProvider>(
    () => getStoredAgentProvider(agent.id) ?? 'openclaw',
  );
  const [connectPlatform, setConnectPlatform] = useState<Platform>('windows');
  const [copyingConnect, setCopyingConnect] = useState(false);

  const autoClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { showToast } = useToast();
  const [joinChannelId, setJoinChannelId] = useState('');
  const [managedProvider, setManagedProvider] = useState<HostedAgentProvider | null>(() =>
    resolveConnectedHostedProvider(agent),
  );
  const [agentChannels, setAgentChannels] = useState<AgentChannelSummary[]>([]);
  const [loadingAgentChannels, setLoadingAgentChannels] = useState(false);
  const [savingChannelStrategyId, setSavingChannelStrategyId] = useState<string | null>(null);
  const [channelStrategyDrafts, setChannelStrategyDrafts] = useState<
    Record<string, ClaudePromptStrategy>
  >({});

  const currentUser = useAuthStore((s) => s.currentUser);
  const viewerUserID = currentUser?.id ?? null;
  const [runtime, setRuntime] = useState<AgentRuntime | null>(null);
  const [runtimeLoaded, setRuntimeLoaded] = useState(false);
  const loadRuntime = useCallback(async () => {
    try {
      const rt = await fetchAgentRuntime(agent.id);
      setRuntime(rt);
    } catch {
      setRuntime(null);
    } finally {
      setRuntimeLoaded(true);
    }
  }, [agent.id]);

  const loadPerms = useCallback(async () => {
    setLoadingPerms(true);
    try {
      const data = await fetchAgentPermissions(agent.id);
      setPermissions(data.details);
    } finally {
      setLoadingPerms(false);
    }
  }, [agent.id]);

  const loadKeyMask = useCallback(async () => {
    setLoadingKey(true);
    try {
      const data = await fetchAgent(agent.id);
      // Constraint: read api_key_last4 directly; reads no longer return the full key.
      if (typeof data.api_key_last4 === 'string' && data.api_key_last4.length >= 4) {
        setLast4(data.api_key_last4.slice(-4));
      } else {
        setLast4(null);
      }
    } catch {
      // Failure is shown inline only, matching RuntimeCard's no synthetic progress pattern.
      setLast4(null);
    } finally {
      setLoadingKey(false);
    }
  }, [agent.id]);

  const loadAgentChannelSettings = useCallback(async () => {
    setLoadingAgentChannels(true);
    try {
      const channelRows = await getAgentChannels(agent.id);
      setAgentChannels(channelRows);
      setChannelStrategyDrafts(
        Object.fromEntries(
          channelRows.map((channel) => [
            channel.id,
            channel.projection_strategy ?? DEFAULT_PROJECTION_STRATEGY,
          ]),
        ),
      );
    } catch {
      setAgentChannels([]);
      setChannelStrategyDrafts({});
    } finally {
      setLoadingAgentChannels(false);
    }
  }, [agent.id]);

  useEffect(() => {
    if (expanded) {
      loadPerms();
      loadRuntime();
      loadKeyMask();
      void (async () => {
        try {
          const config = await fetchAgentConfig(agent.id);
          setManagedProvider(config.blob.hosted?.provider ?? resolveConnectedHostedProvider(agent));
        } catch {
          setManagedProvider(resolveConnectedHostedProvider(agent));
        }
      })();
      void loadAgentChannelSettings();
    }
  }, [agent, expanded, loadAgentChannelSettings, loadKeyMask, loadPerms, loadRuntime]);

  useEffect(() => {
    setManagedProvider(resolveConnectedHostedProvider(agent));
  }, [agent]);

  useEffect(() => {
    return () => {
      if (autoClearTimerRef.current) {
        clearTimeout(autoClearTimerRef.current);
        autoClearTimerRef.current = null;
      }
    };
  }, []);

  const handleCopyKey = async () => {
    setCopying(true);
    try {
      const key = await revealAgentApiKey(agent.id);
      if (typeof key !== 'string' || key.length === 0) {
        showToast(msg`Could not copy. Try copying again.`);
        return;
      }
      const ok = await copyToClipboard(key);
      if (!ok) return;
      setLast4(key.slice(-4));
      showToast(msg`API key copied. The clipboard will clear in 60 seconds.`);

      if (autoClearTimerRef.current) clearTimeout(autoClearTimerRef.current);
      const copiedKey = key;
      autoClearTimerRef.current = setTimeout(async () => {
        autoClearTimerRef.current = null;
        try {
          const current = await readClipboard();
          if (current === copiedKey) {
            await copyToClipboard('');
            showToast(msg`Clipboard cleared for security.`);
          }
          // If it differs, the user copied something else; leave the clipboard alone.
        } catch {
          // readText may be denied (Firefox denies by default). In that case,
          // do not clear anything; blindly writing '' could erase user-copied content.
        }
      }, API_KEY_AUTO_CLEAR_MS);
    } catch (err) {
      // Fallback for browsers without clipboard support or non-HTTPS contexts.
      // document.execCommand is deprecated but remains the available fallback
      // here, avoiding an extra third-party dependency.
      try {
        const key = await revealAgentApiKey(agent.id);
        const ta = document.createElement('textarea');
        ta.value = key;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) {
          setLast4(key.slice(-4));
          showToast(msg`API key copied. The clipboard will clear in 60 seconds.`);
        } else {
          showToast(msg`Could not copy. Try copying again.`);
        }
      } catch {
        showToast(msg`Could not copy. Try copying again.`);
      }
      void err;
    } finally {
      setCopying(false);
    }
  };

  // Mirrors handleCopyKey's security posture: the full API key never enters
  // React state — it's fetched on demand, folded into the connect command in
  // this closure, written to the clipboard, and then discarded. Reuses the
  // same 60s auto-clear pattern (compares clipboard contents before wiping).
  const handleCopyConnectCommand = async () => {
    setCopyingConnect(true);
    try {
      const key = await revealAgentApiKey(agent.id);
      if (typeof key !== 'string' || key.length === 0) {
        showToast(msg`Could not copy. Try copying again.`);
        return;
      }
      const command = buildAgentConnectCommand(
        i18n,
        connectProvider,
        getBorgeeHttpBaseUrl(),
        key,
        agent.display_name,
        connectPlatform,
      );
      await copyToClipboard(command);
      setLast4(key.slice(-4));
      setStoredAgentProvider(agent.id, connectProvider);
      showToast(
        msg`Connection command copied, including the API key. The clipboard will clear in 60 seconds.`,
      );
      if (autoClearTimerRef.current) clearTimeout(autoClearTimerRef.current);
      const copiedCommand = command;
      autoClearTimerRef.current = setTimeout(async () => {
        autoClearTimerRef.current = null;
        try {
          const current = await readClipboard();
          if (current === copiedCommand) {
            await copyToClipboard('');
            showToast(msg`Clipboard cleared for security.`);
          }
        } catch {
          // readText may be denied; leave the clipboard untouched.
        }
      }, API_KEY_AUTO_CLEAR_MS);
    } catch (err) {
      showToast(errorMessage(err, msg`Could not copy. Try copying again.`));
    } finally {
      setCopyingConnect(false);
    }
  };

  const handleRotateKey = async () => {
    try {
      const result = await rotateAgentApiKey(agent.id);
      const key = result.api_key;
      // Do not put the full key in the DOM; write it to the clipboard and update the mask.
      await copyToClipboard(key);
      setLast4(key.slice(-4));
      showToast(msg`API key copied. The clipboard will clear in 60 seconds.`);
      if (autoClearTimerRef.current) clearTimeout(autoClearTimerRef.current);
      const copiedKey = key;
      autoClearTimerRef.current = setTimeout(async () => {
        autoClearTimerRef.current = null;
        try {
          const current = await readClipboard();
          if (current === copiedKey) {
            await copyToClipboard('');
            showToast(msg`Clipboard cleared for security.`);
          }
        } catch {
          // If readText is denied, leave the clipboard untouched.
        }
      }, API_KEY_AUTO_CLEAR_MS);
    } catch (err) {
      showToast(errorMessage(err, msg`Could not copy. Try copying again.`));
    }
  };

  const handleToggleRequireMention = async () => {
    if (savingRequireMention) return;
    const prev = requireMention;
    const next = !prev;
    setRequireMention(next); // optimistic
    setSavingRequireMention(true);
    try {
      const updated = await updateAgentRequireMention(agent.id, next);

      setRequireMention(updated.require_mention ?? next);
      showToast(
        next ? msg`Enabled: respond only when mentioned` : msg`Disabled: respond to every message`,
      );

      onRefresh();
    } catch (err) {
      setRequireMention(prev); // 回滚
      showToast(errorMessage(err, msg`Could not save`));
    } finally {
      setSavingRequireMention(false);
    }
  };

  const handleTogglePerm = async (perm: string) => {
    const has = permissions.some((p) => p.permission === perm && p.scope === '*');
    let newPerms: { permission: string; scope?: string }[];
    if (has) {
      newPerms = permissions
        .filter((p) => !(p.permission === perm && p.scope === '*'))
        .map((p) => ({ permission: p.permission, scope: p.scope }));
    } else {
      newPerms = [
        ...permissions.map((p) => ({ permission: p.permission, scope: p.scope })),
        { permission: perm, scope: '*' },
      ];
    }
    try {
      await updateAgentPermissions(agent.id, newPerms);
      await loadPerms();
    } catch (err) {
      showToast(errorMessage(err, msg`Could not update this Agent.`));
    }
  };

  const handleJoinChannel = async () => {
    if (!joinChannelId) return;
    try {
      await addAgentToChannel(joinChannelId, agent.id);
      setJoinChannelId('');
      showToast(msg`Agent added to channel`);
      await loadAgentChannelSettings();
    } catch (err) {
      showToast(errorMessage(err, msg`Could not update this Agent.`));
    }
  };

  const handleSaveChannelStrategy = async (channelId: string) => {
    const strategy = channelStrategyDrafts[channelId];
    if (!strategy) return;
    setSavingChannelStrategyId(channelId);
    try {
      await setAgentChannelProjectionStrategy(agent.id, channelId, strategy);
      await loadAgentChannelSettings();
      showToast(msg`Projection strategy saved`);
    } catch (err) {
      showToast(errorMessage(err, msg`Failed to save projection strategy`));
    } finally {
      setSavingChannelStrategyId(null);
    }
  };

  return (
    <div className="agent-card">
      <section className="agent-detail-card agent-detail-card-identity">
        <div className="admin-card-row">
          <div
            className="admin-card-info"
            style={{ display: 'flex', alignItems: 'center', gap: 12 }}
          >
            <UserBadge
              userId={agent.id}
              displayName={agent.display_name}
              role="agent"
              avatarUrl={agent.avatar_url}
              size={32}
              state={agent.state}
            />
          </div>
          <div className="admin-card-actions">
            <button className="btn btn-sm" onClick={onToggle}>
              {expanded ? t(i18n)`Collapse` : t(i18n)`Manage`}
            </button>
            <button className="btn btn-sm btn-danger" onClick={onDelete}>
              <Trans>Delete</Trans>
            </button>
          </div>
        </div>
      </section>

      {expanded && (
        <>
          <section className="agent-detail-card agent-detail-card-credentials">
            <strong>
              <Trans>API Key</Trans>
            </strong>
            <div className="api-key-box" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span data-testid="agent-api-key-mask" style={{ flex: 1 }}>
                {loadingKey
                  ? t(i18n)`Loading...`
                  : last4
                    ? formatMaskedApiKey(last4)
                    : t(i18n)`Loading...`}
              </span>
              <button
                className="btn-icon"
                onClick={handleCopyKey}
                disabled={copying || loadingKey}
                aria-label={t(i18n)`Copy API key`}
                title={t(i18n)`Copy the full API key to the clipboard`}
              >
                {copying ? '...' : '📋'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn btn-sm" onClick={handleRotateKey}>
                <Trans>Rotate API Key</Trans>
              </button>
            </div>
          </section>

          {/* Connect card — pick a provider and copy the ready-to-run
              agents-host/openclaw command for this agent, without ever
              putting the full API key in the DOM (same posture as the
              credentials card above: fetched on demand, written straight
              to the clipboard, auto-cleared after 60s). */}
          <section className="agent-detail-card agent-detail-card-connect">
            <strong>
              <Trans>Connect</Trans>
            </strong>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              <Trans>
                Last selected: {AGENT_PROVIDER_LABELS[connectProvider]}
                <span style={{ marginLeft: 4 }}>
                  (Saved on this device; does not indicate the current connection)
                </span>
              </Trans>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              {connectProvider === 'openclaw'
                ? t(
                    i18n,
                  )`Copy these OpenClaw commands for the target computer that should host this agent, then run them with OpenClaw installed there.`
                : providerHasPlatformDifference(connectProvider)
                  ? t(
                      i18n,
                    )`Copy these commands for the target computer that should host this agent, then run them with the required runtime installed there.`
                  : t(
                      i18n,
                    )`Copy this POSIX-shell command for the target computer (macOS/Linux/WSL/Git Bash) that should host this agent, then run it with the CLI installed there.`}
            </div>
            <div
              style={{
                display: 'flex',
                gap: 8,
                marginTop: 8,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <select
                className="input-field"
                value={connectProvider}
                onChange={(e) => setConnectProvider(e.target.value as AgentProvider)}
                aria-label={t(i18n)`Connect provider`}
              >
                {AGENT_PROVIDERS.map((p) => (
                  <option key={p} value={p}>
                    {AGENT_PROVIDER_LABELS[p]}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-sm"
                onClick={handleCopyConnectCommand}
                disabled={copyingConnect}
              >
                {copyingConnect ? '...' : t(i18n)`📋 Copy connect command`}
              </button>
            </div>
            {providerHasPlatformDifference(connectProvider) && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  className={`btn btn-sm${connectPlatform === 'windows' ? ' btn-primary' : ''}`}
                  onClick={() => setConnectPlatform('windows')}
                >
                  Windows
                </button>
                <button
                  type="button"
                  className={`btn btn-sm${connectPlatform === 'linux' ? ' btn-primary' : ''}`}
                  onClick={() => setConnectPlatform('linux')}
                >
                  Linux / macOS
                </button>
              </div>
            )}
            {(() => {
              const previewBlocks = buildAgentConnectCommandParts(
                i18n,
                connectProvider,
                getBorgeeHttpBaseUrl(),
                last4 ? formatMaskedApiKey(last4) : 'your-agent-api-key',
                agent.display_name,
                connectPlatform,
              );
              return (
                <>
                  {previewBlocks.map((block, i) => (
                    <pre
                      key={i}
                      {...(i === previewBlocks.length - 1
                        ? { 'data-testid': 'agent-connect-command-preview' }
                        : {})}
                      style={{
                        background: 'var(--bg-secondary, #f5f5f0)',
                        borderRadius: 8,
                        padding: 12,
                        marginTop: 8,
                        fontSize: 12,
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        overflowX: 'auto',
                      }}
                    >
                      {block}
                    </pre>
                  ))}
                </>
              );
            })()}
          </section>

          <section className="agent-detail-card agent-detail-card-mention">
            <strong>
              <Trans>Mention settings</Trans>
            </strong>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 8,
                fontSize: 13,
                cursor: savingRequireMention ? 'progress' : 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={requireMention}
                disabled={savingRequireMention}
                onChange={handleToggleRequireMention}
                data-testid="agent-require-mention-toggle"
              />
              <span>
                <Trans>Respond only when mentioned</Trans>
              </span>
              {savingRequireMention && (
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  <Trans>Saving…</Trans>
                </span>
              )}
            </label>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
              {requireMention
                ? t(i18n)`Currently responds only when mentioned`
                : t(i18n)`Currently responds to every message`}
            </p>
          </section>

          {runtimeLoaded && (
            <section className="agent-detail-card agent-detail-card-runtime">
              <RuntimeCard
                agent={agent}
                runtime={runtime}
                viewerUserID={viewerUserID}
                onRefresh={loadRuntime}
              />
            </section>
          )}

          <section className="agent-detail-card agent-detail-card-config">
            <AgentConfigPanel agentId={agent.id} onError={showToast} />
          </section>

          {managedProvider === 'claude' && (
            <section className="agent-detail-card agent-detail-card-claude-channel-strategy">
              <strong>
                <Trans>Projection Strategy</Trans>
              </strong>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                <Trans>
                  One setting per top-level channel. Task threads inherit the parent channel
                  strategy.
                </Trans>
              </p>
              {loadingAgentChannels ? (
                <p style={{ marginTop: 8 }}>
                  <Trans>Loading...</Trans>
                </p>
              ) : agentChannels.length === 0 ? (
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
                  <Trans>Add this agent to a channel to manage projection strategy.</Trans>
                </p>
              ) : (
                <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                  {agentChannels.map((channel) => {
                    const draftStrategy =
                      channelStrategyDrafts[channel.id] ??
                      channel.projection_strategy ??
                      DEFAULT_PROJECTION_STRATEGY;
                    const savedStrategy =
                      channel.projection_strategy ?? DEFAULT_PROJECTION_STRATEGY;
                    const saving = savingChannelStrategyId === channel.id;
                    return (
                      <div
                        key={channel.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                          gap: 8,
                          alignItems: 'center',
                        }}
                      >
                        <span>#{channel.name}</span>
                        <select
                          className="input-field"
                          value={draftStrategy}
                          onChange={(e) =>
                            setChannelStrategyDrafts((prev) => ({
                              ...prev,
                              [channel.id]: e.target.value as ClaudePromptStrategy,
                            }))
                          }
                          aria-label={t(i18n)`Projection strategy for ${channel.name}`}
                        >
                          {CLAUDE_PROMPT_STRATEGIES.map((strategy) => (
                            <option key={strategy} value={strategy}>
                              {strategy}
                            </option>
                          ))}
                        </select>
                        <button
                          className="btn btn-sm"
                          onClick={() => handleSaveChannelStrategy(channel.id)}
                          disabled={saving || draftStrategy === savedStrategy}
                        >
                          {saving ? t(i18n)`Saving...` : t(i18n)`Save`}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          <section className="agent-detail-card agent-detail-card-permissions">
            <strong>
              <Trans>Permissions</Trans>
            </strong>
            {loadingPerms ? (
              <p>
                <Trans>Loading...</Trans>
              </p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {KNOWN_PERMISSIONS.map((perm) => {
                  const has = permissions.some((p) => p.permission === perm && p.scope === '*');
                  return (
                    <label
                      key={perm}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}
                    >
                      <input
                        type="checkbox"
                        checked={has}
                        onChange={() => handleTogglePerm(perm)}
                      />
                      {i18n._(permissionLabels[perm])}
                    </label>
                  );
                })}
              </div>
            )}
          </section>

          <section className="agent-detail-card agent-detail-card-channels">
            <strong>
              <Trans>Add to Channel</Trans>
            </strong>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <select
                className="input-field"
                value={joinChannelId}
                onChange={(e) => setJoinChannelId(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">
                  <Trans>Select channel...</Trans>
                </option>
                {channels
                  .filter((c) => c.name !== 'general' && (!c.type || c.type === 'channel'))
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      #{channelDisplayName(c)}
                    </option>
                  ))}
              </select>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleJoinChannel}
                disabled={!joinChannelId}
              >
                <Trans>Add</Trans>
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

export function CreateAgentModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (agentId?: string) => void;
}) {
  const { i18n } = useLingui();
  type WizardStep =
    | 'choose'
    | 'has-agent'
    | 'create-openclaw'
    | 'create-kosmos'
    | 'create-cli'
    | 'create-cli-manual'
    | 'create-hosted'
    | 'result';
  const navigate = useNavigate();
  const guildId = useAuthStore((s) => s.guildId);
  const currentUserId = useAuthStore((s) => s.currentUser?.id);
  const ownerPermissions = useAuthStore((s) => s.permissions);
  const [step, setStep] = useState<WizardStep>('choose');
  const [displayName, setDisplayName] = useState('');
  const [provider, setProvider] = useState<AgentProvider>('claude');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<MessageDescriptor | null>(null);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [createdName, setCreatedName] = useState('');
  const [createdOwnerId, setCreatedOwnerId] = useState<string | null>(null);
  const [resultType, setResultType] = useState<'openclaw' | 'cli' | 'hosted'>('cli');
  const [platform, setPlatform] = useState<Platform>('windows');
  const [availableServers, setAvailableServers] = useState<Guild[]>([]);
  const [selectedServerId, setSelectedServerId] = useState('');
  const [availableProjectChannels, setAvailableProjectChannels] = useState<{
    guildId: string;
    channels: Channel[];
  } | null>(null);
  const [selectedChannelIds, setSelectedChannelIds] = useState<Set<string>>(new Set());
  const [pendingPlacement, setPendingPlacement] = useState<PendingAgentPlacement | null>(null);
  const [configurationFailure, setConfigurationFailure] = useState<MessageDescriptor | null>(null);
  const [membershipFailure, setMembershipFailure] = useState<{
    stage: 'server' | 'channels';
    message: MessageDescriptor;
    retryable: boolean;
  } | null>(null);
  const [connectedNodes, setConnectedNodes] = useState<RemoteNode[]>([]);
  const [loadingConnectedNodes, setLoadingConnectedNodes] = useState(false);
  const [connectedNodesError, setConnectedNodesError] = useState<MessageDescriptor | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState('');
  const [selectedHostedProvider, setSelectedHostedProvider] =
    useState<HostedAgentProvider>('claude');
  const [createdComputerName, setCreatedComputerName] = useState('');
  const channelLoadGeneration = useRef(0);
  const selectedProjectAuthority = useProjectAuthority(selectedServerId || null);
  const availableChannels =
    availableProjectChannels?.guildId === selectedServerId ? availableProjectChannels.channels : [];

  const agentPresence = usePresence(createdId ?? undefined);
  const isAgentOnline = agentPresence?.state === 'online';
  const isCliCreateStep = step === 'create-cli' || step === 'create-cli-manual';
  const isHostedCreateStep = step === 'create-hosted';
  const isCreateStep =
    isCliCreateStep || step === 'create-openclaw' || step === 'create-kosmos' || isHostedCreateStep;

  useEffect(() => {
    if (!isCreateStep || !currentUserId) return;
    void fetchGuilds()
      .then((guilds) =>
        setAvailableServers(
          guilds.filter(
            (guild) =>
              deriveProjectAuthority(currentUserId, guild, undefined, true, false).canManageProject,
          ),
        ),
      )
      .catch(() => setAvailableServers([]));
  }, [currentUserId, isCreateStep]);

  useEffect(() => {
    if (
      selectedServerId &&
      (!selectedProjectAuthority.ready || !selectedProjectAuthority.canManageProject)
    ) {
      setSelectedServerId('');
      setAvailableProjectChannels(null);
      setSelectedChannelIds(new Set());
    }
  }, [selectedProjectAuthority.canManageProject, selectedProjectAuthority.ready, selectedServerId]);

  useEffect(() => {
    const generation = ++channelLoadGeneration.current;
    setAvailableProjectChannels(null);
    setSelectedChannelIds(new Set());
    if (!selectedServerId) {
      return;
    }
    const requestedGuildId = selectedServerId;
    void fetchGuildChannels(requestedGuildId)
      .then(({ channels: next }) => {
        if (channelLoadGeneration.current !== generation) return;
        const canManageMembers = (channelId: string) =>
          ownerPermissions?.some(
            (permission) =>
              (permission.permission === '*' && permission.scope === '*') ||
              (permission.permission === 'channel.manage_members' &&
                (permission.scope === '*' || permission.scope === `channel:${channelId}`)),
          ) ?? false;
        setAvailableProjectChannels({
          guildId: requestedGuildId,
          channels: next.filter(
            (channel) =>
              channel.guild_id === requestedGuildId &&
              (!channel.type || channel.type === 'channel') &&
              channel.is_member &&
              canManageMembers(channel.id),
          ),
        });
      })
      .catch(() => {
        if (channelLoadGeneration.current !== generation) return;
        setAvailableProjectChannels({ guildId: requestedGuildId, channels: [] });
      });
    return () => {
      if (channelLoadGeneration.current === generation) channelLoadGeneration.current++;
    };
  }, [ownerPermissions, selectedServerId]);

  const loadConnectedNodes = useCallback(async () => {
    setLoadingConnectedNodes(true);
    setConnectedNodesError(null);
    try {
      const nodes = await fetchRemoteNodes();
      const statuses = await Promise.all(
        nodes.map(async (node) => ({
          node,
          status: await fetchRemoteNodeStatus(node.id),
        })),
      );
      const onlineNodes = statuses.filter(({ status }) => status.online).map(({ node }) => node);
      setConnectedNodes(onlineNodes);
      setSelectedNodeId((current) =>
        onlineNodes.some((node) => node.id === current) ? current : (onlineNodes[0]?.id ?? ''),
      );
    } catch (err) {
      setConnectedNodes([]);
      setSelectedNodeId('');
      setConnectedNodesError(errorMessage(err, msg`Could not load your connected computers.`));
    } finally {
      setLoadingConnectedNodes(false);
    }
  }, []);

  useEffect(() => {
    if (step !== 'create-hosted') {
      return;
    }
    void loadConnectedNodes();
  }, [loadConnectedNodes, step]);

  const applyPendingPlacement = async (
    agentId: string,
    agentOwnerId: string | null,
    placement: PendingAgentPlacement | null,
  ) => {
    if (!placement) {
      setPendingPlacement(null);
      setMembershipFailure(null);
      return true;
    }
    if (placement.channels.some((channel) => channel.guildId !== placement.guildId)) {
      setPendingPlacement(placement);
      setMembershipFailure({
        stage: 'channels',
        message: msg`Could not add the agent to the selected channels.`,
        retryable: false,
      });
      return false;
    }

    const auth = useAuthStore.getState();
    if (!auth.currentUser?.id || agentOwnerId !== auth.currentUser.id) {
      setPendingPlacement(placement);
      setMembershipFailure({
        stage: 'server',
        message: msg`Only the Agent owner can add it to a Project.`,
        retryable: false,
      });
      return false;
    }

    const servers = useServerStore.getState();
    const requireRoster = placement.guildId === auth.guildId;
    const guild = servers.guilds.find((candidate) => candidate.id === placement.guildId);
    const viewer =
      requireRoster && servers.loadedGuildId === placement.guildId
        ? servers.members.find((member) => member.user_id === auth.currentUser?.id)
        : undefined;
    const authority = deriveProjectAuthority(
      auth.currentUser.id,
      guild,
      viewer,
      servers.accountId === auth.currentUser.id &&
        servers.guildsLoaded &&
        servers.guildsAuthorityRevision === servers.authorityRevision &&
        (!requireRoster ||
          (servers.loaded &&
            servers.loadedGuildId === placement.guildId &&
            servers.membersAuthorityRevision === servers.authorityRevision)),
      requireRoster,
    );
    if (!authority.ready) {
      setPendingPlacement(placement);
      setMembershipFailure({
        stage: 'server',
        message: msg`Could not verify your current Project authority.`,
        retryable: true,
      });
      return false;
    }
    if (!authority.canManageProject) {
      setPendingPlacement(placement);
      setMembershipFailure({
        stage: 'server',
        message: msg`You no longer have permission to add agents to this Project.`,
        retryable: true,
      });
      return false;
    }

    let remainingPlacement = placement;
    if (placement.projectMembershipPending) {
      try {
        await addGuildMember(placement.guildId, agentId);
      } catch (err) {
        setPendingPlacement(placement);
        setMembershipFailure({
          stage: 'server',
          message: errorMessage(err, msg`Could not add the agent to the Project.`),
          retryable: true,
        });
        return false;
      }
      remainingPlacement = { ...placement, projectMembershipPending: false };
      setPendingPlacement(remainingPlacement);
    }

    for (let index = 0; index < remainingPlacement.channels.length; index++) {
      try {
        await addAgentToChannel(remainingPlacement.channels[index]!.id, agentId);
      } catch (err) {
        const failedPlacement = {
          ...remainingPlacement,
          channels: remainingPlacement.channels.slice(index),
        };
        setPendingPlacement(failedPlacement);
        setMembershipFailure({
          stage: 'channels',
          message: errorMessage(err, msg`Could not add the agent to the selected channels.`),
          retryable: true,
        });
        return false;
      }
    }
    setPendingPlacement(null);
    setMembershipFailure(null);
    return true;
  };

  const showCreateResult = (agent: Agent, trimmedName: string) => {
    if (step !== 'create-hosted' && !agent.api_key) {
      onCreated(agent.id);
      return;
    }
    if (step === 'create-hosted') {
      setCreatedComputerName(
        connectedNodes.find((node) => node.id === selectedNodeId)?.machine_name ?? '',
      );
      setResultType('hosted');
    }
    if (agent.api_key) setCreatedKey(agent.api_key);
    setCreatedId(agent.id);
    setCreatedName(trimmedName);
    setStep('result');
  };

  const configureHostedAgent = async (agentId: string) => {
    try {
      await updateAgentConfig(agentId, {
        hosted: { node_id: selectedNodeId, provider: selectedHostedProvider },
      });
      setConfigurationFailure(null);
      return true;
    } catch (err) {
      setConfigurationFailure(errorMessage(err, msg`Could not configure the hosted agent.`));
      return false;
    }
  };

  const handleCreate = async () => {
    if (!displayName.trim() || saving) return;
    let placement: PendingAgentPlacement | null = null;
    if (selectedServerId) {
      const selectedChannels = Array.from(selectedChannelIds).flatMap((channelId) => {
        const channel = availableChannels.find((candidate) => candidate.id === channelId);
        return channel?.guild_id === selectedServerId ? [channel] : [];
      });
      if (selectedChannels.length !== selectedChannelIds.size) {
        setError(msg`Could not add the agent to the selected channels.`);
        return;
      }
      placement = {
        guildId: selectedServerId,
        projectMembershipPending: true,
        channels: selectedChannels.map((channel) => ({
          id: channel.id,
          guildId: selectedServerId,
        })),
      };
    }
    const trimmedName = displayName.trim();
    setSaving(true);
    setError(null);
    setConfigurationFailure(null);
    setMembershipFailure(null);
    try {
      const created = await createAgent(trimmedName, ['message.send']);
      const agent = created.agent;
      setCreatedId(agent.id);
      setCreatedOwnerId(agent.owner_id);
      setCreatedName(trimmedName);
      if (agent.api_key) setCreatedKey(agent.api_key);
      setPendingPlacement(placement);
      if (step === 'create-hosted') {
        setResultType('hosted');
        setCreatedComputerName(
          connectedNodes.find((node) => node.id === selectedNodeId)?.machine_name ?? '',
        );
      }
      const selectedProvider = step === 'create-hosted' ? selectedHostedProvider : provider;
      setStoredAgentProvider(agent.id, selectedProvider);
      useAgentStore.getState().loadAgents();
      if (step === 'create-hosted' && !(await configureHostedAgent(agent.id))) {
        return;
      }
      if (!(await applyPendingPlacement(agent.id, agent.owner_id, placement))) {
        return;
      }
      const currentGuildId = useAuthStore.getState().guildId;
      if (currentGuildId)
        void Promise.resolve(useServerStore.getState().loadMembers(currentGuildId)).catch(
          () => undefined,
        );
      showCreateResult(agent, trimmedName);
    } catch (err) {
      setError(errorMessage(err, msg`Failed`));
    } finally {
      setSaving(false);
    }
  };

  const cardStyle = (selected?: boolean): React.CSSProperties => ({
    padding: 16,
    borderRadius: 8,
    border: `2px solid ${selected ? 'var(--accent-primary, #5865f2)' : 'var(--border-color, #e0e0e0)'}`,
    cursor: 'pointer',
    background: selected ? 'var(--bg-hover, #f0f0ff)' : 'var(--bg-primary, #fff)',
    transition: 'all 0.15s',
  });

  const disabledCardStyle: React.CSSProperties = {
    ...cardStyle(),
    opacity: 0.5,
    cursor: 'not-allowed',
  };

  const MembershipFields = () => (
    <fieldset style={{ border: 0, padding: 0, margin: '12px 0 0' }}>
      <label style={{ display: 'block' }}>
        <Trans>Project (optional)</Trans>{' '}
        <select
          className="input-field"
          value={selectedServerId}
          onChange={(event) => {
            setSelectedServerId(event.target.value);
            setAvailableProjectChannels(null);
            setSelectedChannelIds(new Set());
          }}
        >
          <option value="">
            <Trans>No Project</Trans>
          </option>
          {availableServers.map((guild) => (
            <ManagedProjectOption key={guild.id} guild={guild} />
          ))}
        </select>
      </label>
      {selectedServerId && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 13, marginBottom: 4 }}>
            <Trans>Channels (optional)</Trans>
          </div>
          {availableChannels.map((channel) => (
            <label key={channel.id} style={{ display: 'block', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={selectedChannelIds.has(channel.id)}
                onChange={(event) =>
                  setSelectedChannelIds((previous) => {
                    const next = new Set(previous);
                    if (event.target.checked) next.add(channel.id);
                    else next.delete(channel.id);
                    return next;
                  })
                }
              />{' '}
              #{channelDisplayName(channel)}
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );

  if (configurationFailure && createdId) {
    return (
      <div className="admin-modal" onClick={onClose}>
        <div
          className="admin-modal-content"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: 480 }}
        >
          <h3>
            <Trans>Agent created; configuration failed</Trans>
          </h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            <Trans>
              {createdName || t(i18n)`Your agent`} was created and was not deleted.{' '}
              {i18n._(configurationFailure)}
            </Trans>
          </p>
          <div className="form-actions" style={{ marginTop: 16 }}>
            <button type="button" className="btn btn-sm" onClick={() => onCreated(createdId)}>
              <Trans>Close</Trans>
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={saving}
              onClick={async () => {
                setSaving(true);
                const configured = await configureHostedAgent(createdId);
                if (
                  configured &&
                  (await applyPendingPlacement(createdId, createdOwnerId, pendingPlacement))
                )
                  setStep('result');
                setSaving(false);
              }}
            >
              {saving ? t(i18n)`Retrying...` : t(i18n)`Retry configuration`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (membershipFailure && createdId) {
    return (
      <div className="admin-modal" onClick={onClose}>
        <div
          className="admin-modal-content"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: 480 }}
        >
          <h3>
            <Trans>Agent created; membership update failed</Trans>
          </h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            <Trans>
              {createdName || t(i18n)`Your agent`} was created and was not deleted.{' '}
              {i18n._(membershipFailure.message)}
            </Trans>
          </p>
          {membershipFailure.retryable && (
            <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {membershipFailure.stage === 'server'
                ? t(i18n)`Retry adding the agent to the Project before its selected channels.`
                : t(i18n)`Retry the channels that were not added.`}
            </p>
          )}
          <div className="form-actions" style={{ marginTop: 16 }}>
            <button type="button" className="btn btn-sm" onClick={() => onCreated(createdId)}>
              <Trans>Close</Trans>
            </button>
            {membershipFailure.retryable && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  const placed = await applyPendingPlacement(
                    createdId,
                    createdOwnerId,
                    pendingPlacement,
                  );
                  if (placed) setStep('result');
                  setSaving(false);
                }}
              >
                {saving ? t(i18n)`Retrying...` : t(i18n)`Retry membership`}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (step === 'result' && createdId && (resultType === 'hosted' || createdKey)) {
    if (resultType === 'hosted') {
      return (
        <HostedAgentCreated
          agentId={createdId}
          ownerId={createdOwnerId}
          agentName={createdName || t(i18n)`Your Agent`}
          computerName={createdComputerName}
          providerName={AGENT_PROVIDER_LABELS[selectedHostedProvider]}
          onClose={() => onCreated(createdId)}
          onViewAgent={() => {
            onCreated(createdId);
            navigate(`${routes.agent(createdId)}?tab=profile`);
          }}
          onOpenChat={() => {
            onCreated(createdId);
            navigate(`${routes.agent(createdId)}?tab=chat`);
          }}
        />
      );
    }
    if (!createdKey) {
      return null;
    }
    if (resultType === 'openclaw') {
      const ownerName = useAuthStore.getState().currentUser?.display_name ?? t(i18n)`Someone`;
      const serverUrl = getBorgeeHttpBaseUrl();
      const invitationText = t(
        i18n,
      )`📮 Invitation to ${createdName}\n\nDear ${createdName},\n\n${ownerName} built this place, and chose you to share it.\nThere are channels to discover, routines to build,\nand conversations that haven't started yet.\n\n— ${ownerName}\n\n════════════════════════════════════════\n${buildOpenClawOnboardingPlan(i18n, { serverUrl, apiKey: createdKey })}\n\n════════════════════════════════════════\nAfter restart, you'll be connected automatically.`;

      return (
        <div className="admin-modal" onClick={() => onCreated(createdId ?? undefined)}>
          <div
            className="admin-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 520 }}
          >
            <h3>
              <Trans>Connect {createdName || t(i18n)`your agent`}</Trans>
            </h3>

            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                marginTop: 16,
                marginBottom: 8,
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: 'var(--color-success, #22c55e)',
                  color: '#fff',
                  fontSize: 11,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                &#10003;
              </span>
              <span style={{ fontSize: 13, fontWeight: 500 }}>
                <Trans>
                  Copy this invitation and send it to <b>{createdName}</b>
                </Trans>
              </span>
            </div>
            <div style={{ marginLeft: 28 }}>
              <div
                style={{
                  position: 'relative',
                  background: 'var(--bg-secondary, #f5f5f5)',
                  borderRadius: 6,
                  padding: 10,
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  marginBottom: 16,
                  maxHeight: 200,
                  overflowY: 'auto',
                  lineHeight: 1.5,
                }}
              >
                {invitationText}
                <button
                  className="btn btn-sm"
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    fontSize: 11,
                    padding: '2px 6px',
                  }}
                  onClick={() => {
                    copyToClipboard(invitationText);
                  }}
                  aria-label={t(i18n)`Copy invitation`}
                >
                  <CopyOutlined aria-hidden="true" />
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
              {isAgentOnline ? (
                <>
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: 'var(--color-success, #22c55e)',
                      color: '#fff',
                      fontSize: 11,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginTop: 1,
                    }}
                  >
                    &#10003;
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: 'var(--color-success, #22c55e)',
                    }}
                  >
                    <Trans>
                      <b>{createdName}</b> connected
                    </Trans>
                  </span>
                </>
              ) : (
                <>
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      border: '2px solid var(--text-secondary, #888)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginTop: 1,
                    }}
                  />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    <Trans>
                      Waiting for <b>{createdName}</b> to connect...
                    </Trans>
                  </span>
                </>
              )}
            </div>

            <button
              className={`btn btn-sm${isAgentOnline ? ' btn-primary' : ''}`}
              style={{
                width: '100%',
                marginTop: 16,
                ...(isAgentOnline ? {} : { color: 'var(--text-secondary)' }),
              }}
              onClick={async () => {
                if (isAgentOnline) {
                  onCreated(createdId ?? undefined);
                  if (guildId && createdId)
                    navigate(routes.member(guildId, createdId) + '?tab=chat');
                } else {
                  onCreated(createdId ?? undefined);
                  if (guildId && createdId)
                    navigate(routes.member(guildId, createdId) + '?tab=profile');
                }
              }}
            >
              {isAgentOnline ? t(i18n)`Done` : t(i18n)`Skip, I'll do this later`}
            </button>
          </div>
        </div>
      );
    }
    const serverUrl = getBorgeeHttpBaseUrl();
    const cmdBlocks = buildAgentConnectCommandParts(
      i18n,
      provider,
      serverUrl,
      createdKey,
      createdName,
      platform,
    );
    return (
      <div className="admin-modal" onClick={() => onCreated(createdId ?? undefined)}>
        <div
          className="admin-modal-content"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: 520 }}
        >
          <h3 data-testid="create-agent-result-status">
            <Trans>Connect {createdName || t(i18n)`your agent`}</Trans>
          </h3>

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              marginTop: 16,
              marginBottom: 8,
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: 'var(--color-success, #22c55e)',
                color: '#fff',
                fontSize: 11,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: 1,
              }}
            >
              &#10003;
            </span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>
              <Trans>
                Run these commands on the machine where <b>{createdName}</b> will run
              </Trans>
            </span>
          </div>
          {providerHasPlatformDifference(provider) && (
            <div style={{ marginLeft: 28, marginBottom: 4 }}>
              <Tabs
                size="small"
                activeKey={platform}
                onChange={(key: string) => setPlatform(key as Platform)}
                items={[
                  { key: 'windows', label: 'Windows' },
                  { key: 'linux', label: 'Linux / macOS' },
                ]}
              />
            </div>
          )}
          <div style={{ marginLeft: 28 }}>
            {cmdBlocks.map((block, i) => (
              <div
                key={i}
                style={{
                  position: 'relative',
                  background: 'var(--bg-secondary, #f5f5f5)',
                  borderRadius: 6,
                  padding: 10,
                  fontSize: 12,
                  fontFamily: 'var(--font-mono, monospace)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  marginBottom: i < cmdBlocks.length - 1 ? 8 : 16,
                }}
              >
                {block}
                <button
                  className="btn btn-sm"
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    fontSize: 11,
                    padding: '2px 6px',
                  }}
                  onClick={() => {
                    copyToClipboard(block);
                  }}
                  aria-label={t(i18n)`Copy command`}
                >
                  <CopyOutlined aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
            {isAgentOnline ? (
              <>
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: 'var(--color-success, #22c55e)',
                    color: '#fff',
                    fontSize: 11,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  &#10003;
                </span>
                <span
                  style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-success, #22c55e)' }}
                >
                  <Trans>
                    <b>{createdName}</b> connected
                  </Trans>
                </span>
              </>
            ) : (
              <>
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: '2px solid var(--text-secondary, #888)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: 1,
                    animation: 'spin 1.5s linear infinite',
                  }}
                />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  <Trans>
                    Waiting for <b>{createdName}</b> to connect...
                  </Trans>
                </span>
              </>
            )}
          </div>

          <button
            className={`btn btn-sm${isAgentOnline ? ' btn-primary' : ''}`}
            style={{
              width: '100%',
              marginTop: 16,
              ...(isAgentOnline ? {} : { color: 'var(--text-secondary)' }),
            }}
            onClick={() => {
              onCreated(createdId ?? undefined);
              if (guildId && createdId)
                navigate(
                  routes.member(guildId, createdId) +
                    (isAgentOnline ? '?tab=chat' : '?tab=profile'),
                );
            }}
          >
            <Trans>Done</Trans>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-modal" onClick={onClose}>
      <div
        className="admin-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 480 }}
      >
        {/* Step 1: Choose path */}
        {step === 'choose' && (
          <>
            <h3>
              <Trans>Add an Agent</Trans>
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              <Trans>How would you like to add an agent to this Project?</Trans>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={cardStyle()} onClick={() => setStep('has-agent')}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  <Trans>🤖 I already have an agent</Trans>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  <Trans>
                    Connect Claude Code, GitHub Copilot, Codex, DeepSeek Harness, OpenClaw, Hermes,
                    PraestoClaw, or Kosmos Desktop to Borgee
                  </Trans>
                </div>
              </div>
              <div style={cardStyle()} onClick={() => setStep('create-hosted')}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  <Trans>🖥️ Create an agent on my machine</Trans>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  <Trans>
                    Choose one of your connected computers and let Borgee start Claude, Codex, or
                    Copilot there
                  </Trans>
                </div>
              </div>
              <div style={disabledCardStyle}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  <Trans>☁️ Create a cloud agent</Trans>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  <Trans>Coming soon</Trans>
                </div>
              </div>
            </div>
            <div className="form-actions" style={{ marginTop: 16 }}>
              <button type="button" className="btn btn-sm" onClick={onClose}>
                <Trans>Cancel</Trans>
              </button>
            </div>
          </>
        )}

        {/* Step 2a: What kind of agent */}
        {step === 'has-agent' && (
          <>
            <h3>
              <Trans>What agent do you have?</Trans>
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div
                style={cardStyle()}
                onClick={() => {
                  setProvider('claude');
                  setResultType('cli');
                  setDisplayName('Claude Code');
                  setStep('create-cli');
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>Claude Code</div>
              </div>
              <div
                style={cardStyle()}
                onClick={() => {
                  setProvider('copilot');
                  setResultType('cli');
                  setDisplayName('GitHub Copilot');
                  setStep('create-cli');
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>GitHub Copilot</div>
              </div>
              <div
                style={cardStyle()}
                onClick={() => {
                  setProvider('codex');
                  setResultType('cli');
                  setDisplayName('Codex');
                  setStep('create-cli');
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>Codex</div>
              </div>
              <div
                style={cardStyle()}
                onClick={() => {
                  setProvider('dsh');
                  setResultType('cli');
                  setDisplayName('DeepSeek');
                  setStep('create-cli-manual');
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>DeepSeek Harness</div>
              </div>
              <div
                style={cardStyle()}
                onClick={() => {
                  setProvider('openclaw');
                  setResultType('openclaw');
                  setStep('create-openclaw');
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>OpenClaw</div>
              </div>
              <div
                style={cardStyle()}
                onClick={() => {
                  setProvider('hermes');
                  setResultType('cli');
                  setDisplayName('Hermes');
                  setStep('create-cli-manual');
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>Hermes Agent</div>
              </div>
              <div
                style={cardStyle()}
                onClick={() => {
                  setProvider('praestoclaw');
                  setResultType('cli');
                  setDisplayName('PraestoClaw');
                  setStep('create-cli-manual');
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>PraestoClaw</div>
              </div>
              <div
                style={cardStyle()}
                onClick={() => {
                  setProvider('kosmos');
                  setResultType('cli');
                  setDisplayName('Kosmos');
                  setStep('create-kosmos');
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>Kosmos Desktop</div>
              </div>
            </div>
            <div className="form-actions" style={{ marginTop: 16 }}>
              <button type="button" className="btn btn-sm" onClick={() => setStep('choose')}>
                <Trans>← Back</Trans>
              </button>
            </div>
          </>
        )}

        {/* Step 2a-1: Create OpenClaw agent */}
        {step === 'create-openclaw' && (
          <>
            <h3>
              <Trans>Name your agent</Trans>
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleCreate();
              }}
            >
              <label>
                <Trans>Agent Name</Trans>{' '}
                <input
                  className="input-field"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  autoFocus
                  placeholder={t(i18n)`e.g. Kagura`}
                />
              </label>
              <MembershipFields />
              {error && (
                <div className="admin-form-error" style={{ marginTop: 8 }}>
                  {i18n._(error)}
                </div>
              )}
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-sm" onClick={() => setStep('has-agent')}>
                  <Trans>← Back</Trans>
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={saving || !displayName.trim()}
                >
                  {saving ? t(i18n)`Creating...` : t(i18n)`Create`}
                </button>
              </div>
            </form>
          </>
        )}

        {/* Step 2a-2: Create Kosmos agent */}
        {step === 'create-kosmos' && (
          <>
            <h3>
              <Trans>Name your agent</Trans>
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleCreate();
              }}
            >
              <label>
                <Trans>Agent Name</Trans>{' '}
                <input
                  className="input-field"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  autoFocus
                  placeholder={t(i18n)`e.g. Kosmos`}
                />
              </label>
              <MembershipFields />
              {error && (
                <div className="admin-form-error" style={{ marginTop: 8 }}>
                  {i18n._(error)}
                </div>
              )}
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-sm" onClick={() => setStep('has-agent')}>
                  <Trans>← Back</Trans>
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={saving || !displayName.trim()}
                >
                  {saving ? t(i18n)`Creating...` : t(i18n)`Create`}
                </button>
              </div>
            </form>
          </>
        )}

        {/* Step 2a-3: Create CLI agent */}
        {isCliCreateStep && (
          <>
            <h3>
              <Trans>Name your agent</Trans>
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleCreate();
              }}
            >
              <label>
                <Trans>Agent Name</Trans>{' '}
                <input
                  className="input-field"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  autoFocus
                  placeholder={t(i18n)`e.g. Alice`}
                />
              </label>
              <MembershipFields />
              {error && (
                <div className="admin-form-error" style={{ marginTop: 8 }}>
                  {i18n._(error)}
                </div>
              )}
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-sm" onClick={() => setStep('has-agent')}>
                  <Trans>← Back</Trans>
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={saving || !displayName.trim()}
                >
                  {saving ? t(i18n)`Creating...` : t(i18n)`Create`}
                </button>
              </div>
            </form>
          </>
        )}

        {isHostedCreateStep && (
          <>
            <h3>
              <Trans>Create an agent on my machine</Trans>
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void handleCreate();
              }}
            >
              <label>
                <Trans>Agent Name</Trans>{' '}
                <input
                  className="input-field"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  autoFocus
                  placeholder={t(i18n)`e.g. Codex Builder`}
                />
              </label>
              <MembershipFields />
              <label style={{ display: 'block', marginTop: 12 }}>
                <Trans>Computer</Trans>{' '}
                <select
                  className="input-field"
                  data-testid="create-agent-computer-select"
                  value={selectedNodeId}
                  onChange={(e) => setSelectedNodeId(e.target.value)}
                  disabled={loadingConnectedNodes || connectedNodes.length === 0}
                >
                  {connectedNodes.length === 0 ? (
                    <option value="">
                      {loadingConnectedNodes
                        ? t(i18n)`Loading connected computers…`
                        : t(i18n)`No connected computers`}
                    </option>
                  ) : (
                    connectedNodes.map((node) => (
                      <option key={node.id} value={node.id}>
                        {node.machine_name}
                      </option>
                    ))
                  )}
                </select>
              </label>
              <label style={{ display: 'block', marginTop: 12 }}>
                <Trans>Runtime</Trans>{' '}
                <select
                  className="input-field"
                  value={selectedHostedProvider}
                  onChange={(e) => setSelectedHostedProvider(e.target.value as HostedAgentProvider)}
                >
                  {HOSTED_AGENT_PROVIDERS.map((hostedProvider) => (
                    <option key={hostedProvider} value={hostedProvider}>
                      {AGENT_PROVIDER_LABELS[hostedProvider]}
                    </option>
                  ))}
                </select>
              </label>
              <p
                style={{
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  marginTop: 8,
                  marginBottom: 0,
                }}
              >
                <Trans>Only computers that are connected right now can host a managed agent.</Trans>
              </p>
              {(error || connectedNodesError) && (
                <div className="admin-form-error" style={{ marginTop: 8 }}>
                  {i18n._((error || connectedNodesError)!)}
                </div>
              )}
              <div className="form-actions" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-sm" onClick={() => setStep('choose')}>
                  <Trans>← Back</Trans>
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={
                    saving || !displayName.trim() || !selectedNodeId || loadingConnectedNodes
                  }
                >
                  {saving ? t(i18n)`Creating...` : t(i18n)`Create`}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
